/**
 * **动态音乐的评分系统：`energy` / `isDying` / `isFinal`。**
 *
 * BGM 不是一个开关，而是一组**连续变化的参数**（`ZenithBgmParams`）。
 * 这个文件负责把"局面"翻译成那组参数 —— 于是"什么时候音乐变紧张"这件事
 * 是可读、可测、可调的，而不是散在界面里的几个 if。
 *
 * ## 三个量的分工
 *
 * | 量 | 是什么 | 变化方式 |
 * | --- | --- | --- |
 * | `energy` | **紧张度**。小数、可以是负数；决定随机装饰层开几层 | 每次局面变化重算 |
 * | `isDying` | **只在濒死期间为真** | 跟着抉择走 |
 * | `isFinal` | **终局标记**，一旦为真就不再回退 | 有黏性（见下） |
 *
 * ## 评分表（评审给的，逐条对照）
 *
 * | 项 | 值 |
 * | --- | --- |
 * | 基线 | 菜单里恒为 **0**；进对局 **+2** |
 * | 手上每张牌 | **−0.5**（牌多 = 安全） |
 * | 手上每张救命牌 | **再额外 −3** |
 * | 每出局一名玩家 | **+1** |
 * | 本局每抽到过一次「异常」 | **+0.5** |
 * | 剩余抽牌堆比例 | 100% → **0**；20% 以下 → **+8**（中间线性） |
 * | 对手总牌数 / 对手人数 / 3 | **无条件加** |
 * | 濒死 | 若 < 6 则**设为 6** |
 * | 最后 | **向下取整** |
 *
 * ⚠️ **两处推论**（评审没写死，我按"最平滑"的读法定的，用之前先看这两条）：
 *
 * 1. **牌堆那一项中间是线性插值。** 评审只给了两个锚点（100% → 0、
 *    低于 20% → 8）。取线性是因为"动态音乐"要的是**平滑**：写成阶跃的话
 *    音乐会在 20% 那一刻突然跳一档。低于 20% 则平在 8。
 * 2. **濒死的下限在取整之前施加。** 原文的顺序就是"先设为 6、再取整"，
 *    两条一起作用的结果是：濒死时 `energy` 至少是 6（整数）。
 *
 * ## `isFinal` 的黏性
 *
 * 「一旦为真，直至游戏结束前不应设为假」。触发有两条路：
 *
 * 1. **活着的玩家减少到 2 人**（从 3 人及以上掉到 2 人）。
 *    ⚠️ 二人局**不**因此开局就是终局 —— 否则下面第 2 条就没意义了
 * 2. **二人局里，濒死发生且当时 `energy > 8`** —— 濒死结束之后永久为真
 *
 * 第 2 条是"一局只剩两个人、而且局面已经紧到极点"的那种终局感。
 *
 * ## 为什么要一个 tracker 而不只是一个纯函数
 *
 * 因为有三样东西**不属于任何一帧**：`isFinal` 的黏性、二人局那个"濒死期间
 * 见过高能量"的闩、以及本局累计抽到过几次异常。它们跨帧活着，所以得有人记。
 * 计算本身仍然是纯的（`computeEnergy`），tracker 只负责那三样记忆。
 */

import type { ZenithBgmParams } from './score.zenith';

// ============================================================
// 评分
// ============================================================

/**
 * 评分表的常数。
 *
 * ⚠️ 全部摆在这儿而不是散在算式里 —— "调紧张度"是迟早会发生的事，
 * 而散落的字面量会让那件事变成一次考古。
 */
export const ENERGY: {
  readonly ENTER_GAME: number;
  readonly PER_HAND_CARD: number;
  readonly PER_RESCUE_EXTRA: number;
  readonly PER_ELIMINATED: number;
  readonly PER_EXCEPTION_DRAW: number;
  readonly EMPTY_PILE: number;
  readonly PILE_FLAT_AT: number;
  readonly DYING_FLOOR: number;
  readonly MAX: number;
} = {
  /** 进对局的基线 */
  ENTER_GAME: 2,
  /** 手上每张牌 */
  PER_HAND_CARD: -0.5,
  /** 手上每张救命牌**额外**扣的 */
  PER_RESCUE_EXTRA: -3,
  /** 每出局一名玩家 */
  PER_ELIMINATED: 1,
  /** 本局每抽到过一次异常 */
  PER_EXCEPTION_DRAW: 0.5,
  /** 牌堆见底时的峰值 */
  EMPTY_PILE: 8,
  /** 牌堆比例到这个值及以下就平在峰值 */
  PILE_FLAT_AT: 0.2,
  /** 濒死时的下限 */
  DYING_FLOOR: 6,
  /** 传给 BGM 之前夹到的上限 */
  MAX: 6,
} as const;

/**
 * 一次评分要的全部事实。
 *
 * ⚠️ 它是一份**快照**，不是 GameState —— 界面把视图里能读到的数字填进来，
 * 于是评分表能在 node 里逐条验，不用起浏览器。
 */
export interface EnergyInput {
  /** 在不在对局里。**菜单里 `energy` 恒为 0** */
  readonly inGame: boolean;
  /** 我的手牌张数 */
  readonly handSize: number;
  /** 其中是救命牌的（`traits.rescue`）有几张 */
  readonly rescueInHand: number;
  /** **开局时**的玩家总数。判"是不是二人局"用它 */
  readonly rosterSize: number;
  /** 现在还活着的玩家数 */
  readonly aliveCount: number;
  /** 剩余抽牌堆张数 */
  readonly drawPileCount: number;
  /** 对手的手牌总数 */
  readonly opponentCards: number;
  /** 活着的对手人数 */
  readonly opponentCount: number;
  /** 我是不是正在濒死（抽到异常、正在决定拆不拆） */
  readonly dying: boolean;
  /** 本局到目前为止抽到过几次异常。由 tracker 累加，见 `noteExceptionDraw()` */
  readonly exceptionDraws: number;
}

/** 剩余牌堆那一项。100% → 0，20% 及以下 → +8，中间线性 */
export function pileEnergy(ratio: number): number {
  if (!Number.isFinite(ratio)) return 0;
  const r = ratio < 0 ? 0 : ratio > 1 ? 1 : ratio;
  if (r >= 1) return 0;
  if (r <= ENERGY.PILE_FLAT_AT) return ENERGY.EMPTY_PILE;
  // 100% → 0、20% → 8 之间的线性插值
  return (ENERGY.EMPTY_PILE * (1 - r)) / (1 - ENERGY.PILE_FLAT_AT);
}

/**
 * **算 `energy`**，返回**向下取整后的真实值**（可能是负数）。
 *
 * ⚠️ 传给 BGM 的要用 `toBgmEnergy()` 夹一道 —— 真实值本身不夹，
 * 因为"负了多少"是有意义的信息（调试页与将来做 HUD 都要看它）。
 */
export function computeEnergy(input: EnergyInput, drawPileBaseline: number | null): number {
  // 菜单里恒为 0 —— 一个字节都不算
  if (!input.inGame) return 0;

  let raw = ENERGY.ENTER_GAME;

  raw += input.handSize * ENERGY.PER_HAND_CARD;
  raw += input.rescueInHand * ENERGY.PER_RESCUE_EXTRA;
  raw += Math.max(0, input.rosterSize - input.aliveCount) * ENERGY.PER_ELIMINATED;
  raw += Math.max(0, input.exceptionDraws) * ENERGY.PER_EXCEPTION_DRAW;

  if (drawPileBaseline !== null && drawPileBaseline > 0) {
    raw += pileEnergy(input.drawPileCount / drawPileBaseline);
  }

  // 无条件加。⚠️ 对手为 0 时这一项算 0 —— 除以 0 会得到 Infinity，
  //    而 Infinity 会一路传到 BGM 的参数里（那边夹得住，但显示会很难看）
  if (input.opponentCount > 0) {
    raw += input.opponentCards / input.opponentCount / 3;
  }

  // 濒死时抬到下限。**在取整之前**
  if (input.dying && raw < ENERGY.DYING_FLOOR) raw = ENERGY.DYING_FLOOR;

  return Math.floor(raw);
}

/** 传给 BGM 的那个值：`clamp(0, 6)`。**真实值不受影响** */
export function toBgmEnergy(energy: number): number {
  if (!Number.isFinite(energy)) return 0;
  return Math.min(ENERGY.MAX, Math.max(0, energy));
}

// ============================================================
// 跨帧的记忆
// ============================================================

export interface EnergySnapshot {
  /** **真实值**（可负、可超过 6） */
  readonly energy: number;
  /**
   * **给画面看的那一份**（背景的亮度 / 色调 / 播放速度读它）。
   *
   * 和 `energy` 的关系就是"同一件事的两个出口"：
   *
   * ```
   *   visual = energy
   *   濒死 或 终局 → max(visual, 2)     （画面上"至少是紧张的"）
   *   终局          → × 1.5
   *   濒死          → × 1.5             （两项都成立就是 × 2.25）
   * ```
   *
   * ⚠️ 它**不动** `energy`（规则 / 音乐 / 诊断读的还是真实值）—— 和
   *    `toBgmParams()` 只在出口上分岔是同一条纪律。这一条原来是 `update()`
   *    里一个算了没人用的局部变量（评审留的 TODO），现在做成了正式输出。
   */
  readonly visualEnergy: number;
  /** 夹过的那一份，传给 BGM */
  readonly bgmEnergy: number;
  readonly isDying: boolean;
  readonly isFinal: boolean;
  /** 本局累计抽到过几次异常。诊断用 */
  readonly exceptionDraws: number;
  /** 开局时的牌堆张数（第一次见到的那一个）。诊断用 */
  readonly drawPileBaseline: number | null;
}

/**
 * **界面能知道的那部分事实。**
 *
 * 和 `EnergyInput` 的差别只有一项：`exceptionDraws`（本局抽到过几次异常）
 * **不属于任何一帧** —— 它是事件累加出来的，所以由 tracker 自己记
 * （`noteExceptionDraw()`），界面不用管。
 */
export type EnergyFacts = Omit<EnergyInput, 'exceptionDraws'>;

/**
 * **界面往这里报告局面。**
 *
 * 界面不做判断，只把"现在是什么样"报上来 —— 于是"音乐什么时候变紧张"
 * 这件事只有一处实现，而且那一处是纯的、能单测的。
 */
export interface EnergyReporter {
  /** 每次局面变化报一次（重复报同一份是安全的，会算出同一个结果） */
  report(facts: EnergyFacts): void;
  /** **事件驱动**：抽到一次异常。一次视图更新里可能发生好几回 */
  noteExceptionDraw(): void;
  /** 本局结束（或回到菜单）：三个量一起归零 */
  reset(): void;
  /**
   * 现在是不是终局。
   *
   * ⚠️ 界面**要读它**：终局里牌面的脉动从"跟小节"改成"跟拍"
   * （见 `ui/beat.ts` 的 `drivesCardPulse()`）。所以这个量不只属于音乐 ——
   * 它也是"画面该怎么跟着音乐动"的一部分。
   *
   * ⚠️ 是**只读**的：它由评分表算出来，界面不许自己判。
   */
  readonly isFinal: boolean;
  /**
   * 现在的 `energy` 原值（**没夹过、没取整之后又变的那个真实值**）。
   *
   * ⚠️ 它是"情绪"的来源：背景的色调、亮度、甚至播放速度都跟着它走
   * （`render/bg/mood.ts`）。那边自己会 `clamp(-10, energy, 12)`，所以这里
   * 交出去的是原始值 —— 夹取规则只有一处，别两边各写一份。
   */
  readonly energy: number;
  /**
   * 我是不是正在濒死。
   *
   * ⚠️ 背景的**濒死层**（`render/dying/`：红斜杠横带 + 呼吸辉光 + 裂缝遮罩）
   * 就靠它开合 —— 宿主每帧问一次（`screenHost.ts`）。
   */
  readonly isDying: boolean;
  /**
   * **给画面看的那一份 energy**（背景的亮度 / 色调 / 播放速度读它）。
   *
   * ⚠️ 和 `energy` 的差别见 `EnergySnapshot.visualEnergy` 那三条规则
   *    （濒死/终局至少 2、终局 ×1.5、濒死 ×1.5）。
   */
  readonly visualEnergy: number;
  /**
   * **暂时压住 `isDying`** —— 抽牌动画还在飞的那几百毫秒。
   *
   * 评审：
   *
   * > 在飞到屏幕中心的动画完成后，所有濒死相关的视觉和 BGM 效果随即生效。
   *
   * 而 `isDying` 从"抽到致命牌"那一刻就是真的（视图里那个抉择已经在了）。
   * 所以界面要在动画开始时把它**压住**、飞到中点时放行 —— 压住和放行都在
   * 这一条出口上，音乐和画面读的是同一个值，不会各响各的。
   *
   * ⚠️ 按住的只是**报出去的那一份**：`snapshot.isDying` 照旧是真的，
   * 评分表自己的判断（比如二人局那个终局闩）不受影响。
   */
  holdDying(hold: boolean): void;
}

export interface EnergyTracker {
  /** 每次局面变化喂一次。返回算好的快照 */
  update(facts: EnergyFacts): EnergySnapshot;
  /** **事件驱动**：抽到一次异常。和视图更新无关，可能一次视图里发生好几回 */
  noteExceptionDraw(): void;
  /** 本局结束（或回到菜单）：一切归零，`isFinal` 也放掉 */
  reset(): void;
  readonly snapshot: EnergySnapshot;
}

const EMPTY: EnergySnapshot = Object.freeze({
  energy: 0,
  visualEnergy: 0,
  bgmEnergy: 0,
  isDying: false,
  isFinal: false,
  exceptionDraws: 0,
  drawPileBaseline: null,
});

export function createEnergyTracker(): EnergyTracker {
  let snapshot: EnergySnapshot = EMPTY;
  let exceptionDraws = 0;
  let drawPileBaseline: number | null = null;
  let isFinal = false;
  /** 二人局里"濒死期间见过 energy > 8"的闩 —— 濒死结束之后才兑现 */
  let sawDyingOverThreshold = false;

  const tracker: EnergyTracker = {
    update(input) {
      const dying = input.inGame && input.dying === true;

      // 牌堆基线：**第一次见到的那一个**。开局那次视图就是满的，
      // 而之后它只会变少（插回异常牌会让它回到接近初始，不会超过）
      if (input.inGame && drawPileBaseline === null && input.drawPileCount > 0) {
        drawPileBaseline = input.drawPileCount;
      }

      const full: EnergyInput = { ...input, dying, exceptionDraws };
      const energy = computeEnergy(full, drawPileBaseline);

      // ---- isFinal：两条触发路径，都是单向的 ----
      const alive = Math.max(0, input.aliveCount);
      if (alive <= 2 && input.rosterSize > 2) {
        // ① 活着的人**已经**只剩两个（或更少）。
        //
        // ⚠️ 判的是"现在的状态"而不是"从 3 掉到 2 的那一次转变"。
        //    第一版盯着转变（要记住上一次的存活人数），于是**中途才接上的
        //    tracker 永远等不到那一次转变** —— 掉线重连、或者界面晚一帧
        //    挂上去，终局标记就丢了。而它是黏的，所以重复判到也无所谓
        isFinal = true;
      }
      if (input.rosterSize === 2 && dying && energy > ENERGY.EMPTY_PILE) {
        // ② 二人局：濒死期间见过高能量，记下来，等濒死**结束**再兑现
        sawDyingOverThreshold = true;
      }
      if (sawDyingOverThreshold && !dying) {
        isFinal = true;
        sawDyingOverThreshold = false;
      }
      if (!input.inGame) isFinal = false;

      snapshot = {
        energy,
        visualEnergy: visualEnergyFor(energy, isFinal, dying),
        bgmEnergy: toBgmEnergy(energy),
        isDying: dying,
        isFinal,
        exceptionDraws,
        drawPileBaseline,
      };
      return snapshot;
    },

    noteExceptionDraw() {
      exceptionDraws += 1;
    },

    reset() {
      snapshot = EMPTY;
      exceptionDraws = 0;
      drawPileBaseline = null;
      isFinal = false;
      sawDyingOverThreshold = false;
    },

    get snapshot() {
      return snapshot;
    },
  };

  return tracker;
}

/**
 * 快照 → BGM 的参数。**唯一的转换入口**
 *
 * ## ⚠️ 濒死期间 `energy` 传 0
 *
 * 评审：「濒死时虽然逻辑上 energy 很高，但是传给 BGM 模块时，忽略 energy 的内容
 * 直接传入 0。（濒死期间本就有很多音效，再叠 energy 太乱了）」
 *
 * 所以这里是**唯一**一处"真实值"和"发给 BGM 的值"分岔的地方：
 *
 * | 谁 | 濒死时看到什么 |
 * | --- | --- |
 * | BGM（这个函数的返回值） | `energy: 0`，`isDying: true` |
 * | `snapshot.energy` / `snapshot.bgmEnergy` | **真实值**，一点没变 |
 * | 判 `isFinal` 的那些规则 | **真实值**（`> 8` 那道闩就在 tracker 里，不经过这里） |
 *
 * 换句话说：这不是"算低了"，而是**只在这一条出口上闭麦**。
 * 别的依赖 `energy` 的判断（二人局的终局闩、调试页读数、将来的 HUD）
 * 都读快照，照旧。
 *
 * ⚠️ 副作用是**濒死结束的那一刻会有一次跳变**：进去时 `energy` 从十几掉到 0、
 * 出来时又跳回去。这是评审要的取舍 —— 濒死那一段本来就该是"另一首曲子"，
 * 而且 `isDying` 本身也在一起变（那个 cue 会切），听感上是一次转场而不是失手。
 * 所以这里**不做渐变**：给 0 就是 0，加个斜坡反而会让"进去的一瞬间"
 * 和"音效进来的一瞬间"对不齐。
 */
/**
 * **给画面看的那一份 energy**（见 `EnergySnapshot.visualEnergy`）。
 *
 * 三条规则，顺序不能换：
 *
 * ```
 *   ① 濒死或终局 → 至少 2      （不然"最后一局/濒死"看起来和平时一样淡）
 *   ② 终局       → × 1.5
 *   ③ 濒死       → × 1.5       （②③ 同时成立 = × 2.25）
 * ```
 *
 * ⚠️ 纯函数、不夹上下限：`energy` 本来就是"可负、可超过 6"的真实值，
 *    夹是**消费端**的事（`bg/mood.ts` 的 `clampEnergy`）。
 */
export function visualEnergyFor(energy: number, isFinal: boolean, isDying: boolean): number {
  let visual = isFinal || isDying ? Math.max(energy, 2) : energy;
  if (isFinal) visual *= 1.5;
  if (isDying) visual *= 1.5;
  return visual;
}

export function toBgmParams(snapshot: EnergySnapshot): ZenithBgmParams {
  return {
    isFinal: snapshot.isFinal,
    isDying: snapshot.isDying,
    energy: snapshot.isDying ? 0 : snapshot.bgmEnergy,
  };
}
